import { v4 as uuidv4 } from 'uuid';

const COOKIE_NAME = 'hs_did';
const STORAGE_KEY = 'hs_did';
const IDB_NAME = 'hs-device';
const IDB_STORE = 'identity';
const CACHE_NAME = 'hs-device';
const CACHE_URL = '/hs-device-id';

function readCookie(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const [rawKey, ...rest] = part.trim().split('=');
    if (rawKey === COOKIE_NAME) {
      return decodeURIComponent(rest.join('=')) || null;
    }
  }
  return null;
}

function writeCookie(id: string) {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(id)}; path=/; max-age=315360000; SameSite=Lax`;
}

function readLocalStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore quota / private mode
  }
}

function openIdb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const request = indexedDB.open(IDB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(IDB_STORE)) {
        request.result.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function readIdb(): Promise<string | null> {
  const db = await openIdb();
  if (!db) {
    return null;
  }
  return new Promise((resolve) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const request = tx.objectStore(IDB_STORE).get(STORAGE_KEY);
    request.onsuccess = () => {
      resolve(typeof request.result === 'string' ? request.result : null);
      db.close();
    };
    request.onerror = () => {
      resolve(null);
      db.close();
    };
  });
}

async function writeIdb(id: string) {
  const db = await openIdb();
  if (!db) {
    return;
  }
  await new Promise<void>((resolve) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(id, STORAGE_KEY);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      resolve();
    };
  });
}

async function readCache(): Promise<string | null> {
  if (typeof caches === 'undefined') {
    return null;
  }
  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(CACHE_URL);
    return response ? response.text() : null;
  } catch {
    return null;
  }
}

async function writeCache(id: string) {
  if (typeof caches === 'undefined') {
    return;
  }
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(CACHE_URL, new Response(id, { status: 200 }));
  } catch {
    // ignore
  }
}

export async function getOrCreateDeviceId(): Promise<string> {
  const [cookie, local, idb, cache] = await Promise.all([
    Promise.resolve(readCookie()),
    Promise.resolve(readLocalStorage()),
    readIdb(),
    readCache(),
  ]);
  const existing = cookie || local || idb || cache || uuidv4();
  await Promise.all([
    writeCookie(existing),
    Promise.resolve(writeLocalStorage(existing)),
    writeIdb(existing),
    writeCache(existing),
  ]);
  return existing;
}
